//#region src/config.ts
/**
* Default Theemo options
*
* @internal
*/
const DEFAULT_OPTIONS = { outDir: "theemo" };
/**
* ID to identify Theemo `<meta>` element
*
* @internal
*/
const THEEMO_CONFIG_ID = "theemo-config";
/**
* Extract the config from `<meta>` element
*
* @param rootElement element to look for the `<meta>` element in
* @returns runtime config
*/
function extractConfig(rootElement = document) {
	const meta = rootElement.querySelector(`meta[name="${THEEMO_CONFIG_ID}"]`);
	const config = meta ? JSON.parse(decodeURI(meta.content)) : {};
	return {
		options: {
			...DEFAULT_OPTIONS,
			...config.options
		},
		themes: config.themes ?? []
	};
}

//#endregion
//#region src/features.ts
/**
* Color Contrasts
*
* @see https://developer.mozilla.org/en-US/docs/Web/CSS/@media/prefers-contrast
*/
const ColorContrast = {
	NoPreference: "no-preference",
	More: "more",
	Less: "less",
	Custom: "custom"
};
/**
* Color Scheme
*
* @see https://developer.mozilla.org/en-US/docs/Web/CSS/@media/prefers-color-scheme
*/
const ColorScheme = {
	Light: "light",
	Dark: "dark"
};
/**
* Motion
*
* @see https://developer.mozilla.org/en-US/docs/Web/CSS/@media/prefers-reduced-motion
*/
const Motion = {
	NoPreference: "no-preference",
	Reduce: "reduce"
};
const BrowserMechanic = {
	ColorScheme: "color-scheme",
	ColorContrast: "color-contrast",
	Motion: "motion"
};
/**
* The principal who is responsible for the value
*
* @see https://theemo.io/design-tokens/traits#principals-and-agents
*/
const Principal = {
	Browser: "browser",
	User: "user"
};
/**
* Verifies if the given feature has the browser as principal that can change
* it based on media query or other mechanics
*
* @see https://theemo.io/design-tokens/traits#principals-and-agents
* @param feature the feature in question
* @returns `true` for a browser feature, otherwise `false`
*/
function isBrowserFeature(feature) {
	return [
		BrowserMechanic.ColorContrast,
		BrowserMechanic.ColorScheme,
		BrowserMechanic.Motion
	].includes(feature.browserFeature);
}
/**
* Checks wether the given feature uses mode behavior
*
* @param feature the feature in question
* @returns `true` for a mode behavior, otherwise `false`
*/
function isModalFeature(feature) {
	return feature.defaultOption !== void 0;
}
/**
* Checks if the feature contains all valid fields to be operational
*
* @param feature the feature in question
* @returns validation result
*/
function validateFeature(feature) {
	const errors = [];
	if (!feature.name) errors.push("Feature is missing a name");
	if (!feature.options) errors.push(`Feature '${feature.name}' requires options`);
	if (!feature.browserFeature && !feature.defaultOption) errors.push(`Feature '${feature.name}' requires 'defaultOption' or 'browserFeature'`);
	if (feature.defaultOption && !feature.options.includes(feature.defaultOption)) errors.push(`Feature '${feature.name}' has 'defaultOption' to be set to '${feature.defaultOption}', which is not allowed. Allowed values are '${feature.options.join(`', '`)}'.`);
	if (feature.browserFeature && !isBrowserFeature(feature)) errors.push(`Feature '${feature.name}' uses invalid browser mechanic: '${feature.browserFeature}' does not exist. Supported values are: '${Object.values(BrowserMechanic).join("', ")}'.`);
	return {
		success: errors.length === 0,
		errors
	};
}

//#endregion
//#region src/manager.ts
const queries = {
	[BrowserMechanic.ColorScheme]: {
		[ColorScheme.Dark]: "(prefers-color-scheme: dark)",
		[ColorScheme.Light]: "(prefers-color-scheme: light)"
	},
	[BrowserMechanic.ColorContrast]: {
		[ColorContrast.NoPreference]: "(prefers-color-contrast: no-preference)",
		[ColorContrast.More]: "(prefers-color-contrast: more)",
		[ColorContrast.Less]: "(prefers-color-contrast: less)",
		[ColorContrast.Custom]: "(prefers-color-contrast: custom)"
	},
	[BrowserMechanic.Motion]: {
		[Motion.NoPreference]: "(prefers-reduced-motion: no-preference)",
		[Motion.Reduce]: "(prefers-reduced-motion)"
	}
};
function match(feature) {
	const values = {};
	for (const [value, query] of Object.entries(queries[feature.browserFeature])) {
		const check = globalThis.matchMedia(query);
		if (check.matches) values[feature.name] = value;
	}
	return values;
}
function setupListeners(features, cb) {
	const disposals = [];
	const values = {};
	for (const feature of features) for (const [value, query] of Object.entries(queries[feature.browserFeature])) {
		const check = globalThis.matchMedia(query);
		const handler = () => cb(match(feature));
		check.addEventListener("change", handler);
		disposals.push(() => check.removeEventListener("change", handler));
		if (check.matches) values[feature.name] = value;
	}
	return {
		values,
		dispose: () => {
			for (const dispose of disposals) dispose();
		}
	};
}
/**
* Manages theming at runtime
*
* - switch themes
* - turn features on and off
*/
var ThemeManager = class {
	#elements = /* @__PURE__ */ new Map();
	#config;
	#options;
	#activeThemeManager = {};
	#defaultFeatureValues = {};
	#browserFeatureValues = {};
	#modeFeatureValues = {};
	/** The active theme */
	activeTheme;
	/** Features of the active theme */
	features = [];
	constructor(options = {}) {
		this.#options = options;
		this.#config = extractConfig();
		for (const link of document.querySelectorAll("head > link")) if (link.dataset.theemo) {
			this.#elements.set(link.dataset.theemo, link);
			link.disabled = link.dataset.theemo !== this.#config.options.defaultTheme;
		}
		if (this.#config.options.defaultTheme) this.switchTheme(this.#config.options.defaultTheme);
	}
	/**
	* List of available themes
	*/
	get themes() {
		return this.#config.themes;
	}
	get #featureValues() {
		return {
			...this.#defaultFeatureValues,
			...this.#browserFeatureValues,
			...this.#modeFeatureValues
		};
	}
	#findFeature(name) {
		const feature = this.features.find((f) => f.name === name);
		if (!feature) throw new Error(`Cannot find feature '${name}': feature doesn't exist`);
		return feature;
	}
	#getPrincipal(featureOrName) {
		const feature = typeof featureOrName === "string" ? this.#findFeature(featureOrName) : featureOrName;
		const overriddenValue = isBrowserFeature(feature) && this.#modeFeatureValues[feature.name];
		const principal = isBrowserFeature(feature) ? overriddenValue ? Principal.User : Principal.Browser : Principal.User;
		return principal;
	}
	/**
	* Set the feature to a value
	*
	* @param featureName the name of the feature
	* @param value the value for that feature
	*/
	setFeature(featureName, value) {
		const feature = this.#findFeature(featureName);
		if (!feature.options.includes(value)) throw new Error(`Cannot set mode '${feature.name}' to '${value}': option doesn't exist`);
		this.#modeFeatureValues[feature.name] = value;
		this.#updateFeatures();
		document.documentElement.setAttribute(`data-theemo-${feature.name}`, value);
		this.#options.featureChanged?.(feature);
	}
	/**
	* Turn off a feature. Revert to its default.
	*
	* @param featureName the name of the feature
	*/
	unsetFeature(featureName) {
		const feature = this.#findFeature(featureName);
		delete this.#modeFeatureValues[feature.name];
		this.#updateFeatures();
		document.documentElement.removeAttribute(`data-theemo-${feature.name}`);
		this.#options.featureChanged?.(feature);
	}
	/**
	* Switch to another theme
	*
	* @param name theme name
	*/
	async switchTheme(name) {
		if (this.activeTheme?.name === name) return;
		const theme = this.themes.find((t) => t.name === name);
		if (!theme) throw new Error(`Cannot switch theme '${name}': theme doesn't exist`);
		await this.#ensureThemeIsLoaded(theme);
		this.#activateTheme(theme);
		if (this.activeTheme) this.#deactivateTheme(this.activeTheme);
		this.#setupDefaultFeatures(theme);
		this.#setupBrowserFeatures(theme);
		this.activeTheme = theme;
		this.#updateFeatures();
		this.#options.themeChanged?.(theme);
	}
	async #ensureThemeIsLoaded(theme) {
		if (!this.#elements.has(theme.name)) await this.#loadTheme(theme);
	}
	#activateTheme(theme) {
		const element = this.#elements.get(theme.name);
		element.disabled = false;
	}
	#setupBrowserFeatures(theme) {
		const browserBrowserFeatures = (theme.features ?? []).filter((element) => isBrowserFeature(element));
		const browser = setupListeners(browserBrowserFeatures, (values) => this.#handleChangeFeatures(values));
		this.#activeThemeManager.teardownListeners = browser.dispose;
		this.#browserFeatureValues = browser.values;
	}
	#handleChangeFeatures(values) {
		const dump = { ...this.#featureValues };
		this.#browserFeatureValues = {
			...this.#browserFeatureValues,
			...values
		};
		this.#updateFeatures();
		const changes = Object.entries(this.#featureValues).filter(([k, v]) => dump[k] !== v).map(([k]) => k);
		for (const featureName of changes) this.#options.featureChanged?.(this.#findFeature(featureName));
	}
	#updateFeatures() {
		this.features = (this.activeTheme?.features ?? []).map((f) => ({
			...f,
			value: this.#featureValues[f.name],
			browserValue: isBrowserFeature(f) ? this.#browserFeatureValues[f.name] : void 0,
			principal: this.#getPrincipal(f)
		}));
	}
	#setupDefaultFeatures(theme) {
		const defaultFeatures = (theme.features ?? []).filter((element) => isModalFeature(element));
		this.#defaultFeatureValues = Object.fromEntries(defaultFeatures.map((f) => [f.name, f.defaultOption]));
	}
	#deactivateTheme(theme) {
		this.#defaultFeatureValues = {};
		this.#browserFeatureValues = {};
		this.#modeFeatureValues = {};
		this.#clearModes(theme);
		this.#elements.get(theme.name).disabled = true;
	}
	#clearModes(theme) {
		for (const feature of theme.features ?? []) document.documentElement.removeAttribute(`data-theemo-${feature.name}`);
	}
	async #loadTheme(theme) {
		const element = await this.#createLinkElement(theme);
		this.#elements.set(theme.name, element);
	}
	#createLinkElement(theme) {
		const linkElement = document.createElement("link");
		linkElement.setAttribute("href", `/${this.#config.options.outDir}/${theme.filename}.css`);
		linkElement.setAttribute("type", "text/css");
		linkElement.setAttribute("rel", "stylesheet");
		linkElement.setAttribute("title", theme.name);
		linkElement.dataset.theemo = theme.name;
		document.head.append(linkElement);
		return new Promise((resolve) => {
			const listener = () => {
				linkElement.removeEventListener("load", listener);
				linkElement.disabled = true;
				resolve(linkElement);
			};
			linkElement.addEventListener("load", listener);
		});
	}
};

//#endregion
//#region src/theme.ts
/**
* Validates a theme for being correct
*
* @example
*
* Check for a valid theme:
*
* ```ts
* const validation = validateTheme(myTheme);
*
* if (validation.success) {
*   // proceed with valid theme
* } else {
*   console.log(validation.errors);
* }
* ```
*
*
* @param pkg the given theme
* @returns the validation result
*/
function validateTheme(theme) {
	const errors = [];
	if (!theme.name) errors.push(`Theme requires 'name'`);
	for (const feature of theme.features ?? []) errors.push(...validateFeature(feature).errors);
	return {
		success: errors.length === 0,
		errors
	};
}

//#endregion
//#region src/package.ts
/**
* The keyword to look for in a `package.json` file
*/
const KEYWORD = "theemo-theme";
/**
* Checks if the given package is a Theemo package
*
* @param pkg the given package
* @returns `true` if it is a Theemo package, otherwise `false`
*/
function isTheemoPackage(pkg) {
	return (pkg.keywords ?? []).includes(KEYWORD);
}
/**
* Validates a package for being correct
*
* @example
*
* Check for a valid package:
*
* ```ts
* const validation = validateTheemoPackage(myPkg);
*
* if (validation.success) {
*   // proceed with valid package
* } else {
*   console.log(validation.errors);
* }
* ```
*
* @param pkg the given package
* @returns the validation result
*/
function validateTheemoPackage(pkg) {
	const errors = [];
	if (!isTheemoPackage(pkg)) errors.push(`Package '${pkg.name}' requires keyword '${KEYWORD}'`);
	if (pkg.theemo) {
		const themeValidation = validateTheme(pkg.theemo);
		if (!pkg.theemo.file) errors.push(`Theemo in package '${pkg.name}' requires 'file'`);
		errors.push(...themeValidation.errors);
	} else errors.push(`Package '${pkg.name}' requires 'theemo' field`);
	return {
		success: errors.length === 0,
		errors
	};
}

//#endregion
export { BrowserMechanic, ColorContrast, ColorScheme, DEFAULT_OPTIONS, Motion, Principal, THEEMO_CONFIG_ID, ThemeManager, isBrowserFeature, isModalFeature, isTheemoPackage, validateTheemoPackage };
//# sourceMappingURL=index.js.map